import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { createMultipleBookings } from '../utils/db/bookings';
import { listCustomers } from '../utils/db/customers';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
// Removed DB fetch for existing bookings; button will filter from uploaded file only
import { toast } from 'react-toastify';
import { downloadSampleExcel } from '../utils/sampleExcelGenerator';
import { Upload, Download, CheckCircle, XCircle, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';

const MultipleBooking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // { id, customer_id, customer_name }
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);

  // Excel date helper: supports both date strings and Excel serial numbers
  const toYmd = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };
  const formatDateFromExcel = (value) => {
    if (!value && value !== 0) return '';
    if (value instanceof Date && !isNaN(value.getTime())) return toYmd(value);
    if (typeof value === 'number' && isFinite(value)) {
      // Excel serial date: days since 1899-12-31 (with 1900 leap year bug). Use 25569 offset for Unix epoch.
      const jsTime = Math.round((value - 25569) * 86400 * 1000);
      const d = new Date(jsTime);
      if (!isNaN(d.getTime())) return toYmd(d);
    }

    // Handle string formats
    const strVal = String(value).trim();

    // Try parsing DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
    // Regex matches start with 1-2 digits, separator, 1-2 digits, separator, 4 digits
    const dmyMatch = strVal.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (dmyMatch) {
      const day = parseInt(dmyMatch[1], 10);
      const month = parseInt(dmyMatch[2], 10);
      const year = parseInt(dmyMatch[3], 10);
      const d = new Date(year, month - 1, day);
      if (!isNaN(d.getTime()) && d.getDate() === day && d.getMonth() === month - 1 && d.getFullYear() === year) {
        return toYmd(d);
      }
    }

    // Fallback: parse as standard string (YYYY-MM-DD, MM/DD/YYYY etc)
    const d = new Date(strVal);
    return isNaN(d.getTime()) ? '' : toYmd(d);
  };

  // Normalize various forms of IDs from Excel and app state for reliable comparisons
  const normalizeId = (value) => {
    if (value === null || value === undefined) return '';
    // Handle numbers from Excel (avoid scientific notation and decimals)
    if (typeof value === 'number') {
      // Convert to integer-like string if it's a whole number
      if (Number.isFinite(value)) {
        const intLike = Math.trunc(value);
        if (intLike === value) return String(intLike).trim().toLowerCase();
      }
      return String(value).trim().toLowerCase();
    }
    return String(value).trim().toLowerCase();
  };

  // Load customers on component mount
  React.useEffect(() => {
    const loadCustomers = async () => {
      if (!user) return;
      try {
        const customerData = await listCustomers(user.id);
        setCustomers(customerData || []);
      } catch (error) {
        console.error('Error loading customers:', error);
        setCustomers([]);
      }
    };
    loadCustomers();
  }, [user]);


  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseExcelFile(selectedFile);
    }
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          alert('No data found in the Excel file. Please ensure the file contains data.');
          return;
        }

        setParsedData(jsonData);
        validateData(jsonData);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        alert('Error parsing Excel file. Please ensure it\'s a valid Excel file.');
        setParsedData([]);
        setValidationResults([]);
      }
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
      setParsedData([]);
      setValidationResults([]);
    };
    reader.readAsArrayBuffer(file);
  };

  const validateData = (data) => {
    if (!data || !Array.isArray(data)) {
      console.error('Invalid data provided for validation');
      return;
    }

    const requiredFields = [
      // 'Customer ID' is not required; we use the selected customer for all rows
      'AWB Number', 'Date', 'Receiver Name',
      'Mode', 'Weight (kg)', 'Amount'
    ];

    const validationResults = data.map((row, index) => {
      const errors = [];
      const warnings = [];

      // Check required fields
      requiredFields.forEach(field => {
        if (!row[field] || row[field].toString().trim() === '') {
          errors.push(`Missing required field: ${field}`);
        }
      });

      // Validate customer selection; we no longer require Customer ID in file
      if (!selectedCustomer) {
        errors.push('Please select a customer above before uploading.');
      }

      // Validate date format
      if (row['Date']) {
        const dateStr = formatDateFromExcel(row['Date']);
        if (!dateStr) {
          errors.push('Invalid date format. Use YYYY-MM-DD or a valid Excel date');
        }
      }

      // Validate numeric fields
      if (row['Weight (kg)'] && isNaN(parseFloat(row['Weight (kg)']))) {
        errors.push('Weight must be a valid number');
      }

      if (row['Amount'] && isNaN(parseFloat(row['Amount']))) {
        errors.push('Amount must be a valid number');
      }

      if (row['Quantity'] && (isNaN(parseInt(row['Quantity'])) || parseInt(row['Quantity']) < 1)) {
        warnings.push('Quantity should be a positive integer (defaulting to 1)');
      }

      if (row['Other Charge'] && isNaN(parseFloat(row['Other Charge']))) {
        warnings.push('Other Charge must be a valid number (defaulting to 0)');
      }

      return {
        rowIndex: index + 1,
        data: row,
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    setValidationResults(validationResults);
  };

  // Re-validate uploaded rows when customer changes, but do not auto-fetch/display
  React.useEffect(() => {
    if (!selectedCustomer) return;
    if (parsedData && parsedData.length > 0) {
      validateData(parsedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  const handleAddBookings = async () => {
    if (!user) return;
    if (!selectedCustomer?.id) {
      toast.error('Please select a customer first.');
      return;
    }
    const validData = validationResults.filter(result => result.isValid);
    if (validData.length === 0) {
      toast.error('No valid data to add. Please fix the errors first.');
      return;
    }
    setIsUploading(true);
    try {
      const bookings = validData.map(result => {
        const row = result.data;
        return {
          customerId: selectedCustomer.id,
          awbNo: row['AWB Number'].toString(),
          date: formatDateFromExcel(row['Date']),
          receiverName: row['Receiver Name'],
          receiverAddress: row['Receiver Address'] || '',
          pincode: row['Pincode'] || '',
          mode: row['Mode'],
          type: row['Type'] || '',
          weight: parseFloat(row['Weight (kg)']),
          quantity: parseInt(row['Quantity']) || 1,
          amount: parseFloat(row['Amount']),
          otherCharge: parseFloat(row['Other Charge']) || 0
        };
      });
      const results = await createMultipleBookings(user.id, bookings);
      setUploadResults({ success: results.success, failed: results.failed, total: validData.length });
      if (results.success > 0 && results.failed === 0) {
        toast.success(`Added ${results.success} booking(s).`);
      } else if (results.success > 0 && results.failed > 0) {
        toast.warn(`Added ${results.success} booking(s), ${results.failed} failed.`);
      } else {
        toast.error(`Failed to add bookings. ${results.errors?.[0] || ''}`);
      }
      // Refresh existing bookings list
      await fetchCustomerBookings();
    } catch (error) {
      console.error('Error adding bookings:', error);
      toast.error(`Error adding bookings: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const fetchCustomerBookings = async () => {
    if (!user) return;
    if (!selectedCustomer?.id) {
      alert('Please select a customer first.');
      return;
    }
    if (!parsedData || parsedData.length === 0) {
      alert('Please upload an Excel file first.');
      return;
    }
    try {
      const selectedIdOptions = [
        normalizeId(selectedCustomer.customer_id),
        normalizeId(selectedCustomer.id)
      ].filter(Boolean);
      const rowsForCustomer = parsedData.filter(row => {
        const rowCustomerId = normalizeId(row['Customer ID']);
        return selectedIdOptions.includes(rowCustomerId);
      });

      // Normalize to the display shape used in the table below
      const normalized = rowsForCustomer.map((row, index) => ({
        id: index,
        awb_no: row['AWB Number'],
        date: row['Date'],
        receiver_name: row['Receiver Name'],
        amount: row['Amount']
      }));

      setExistingBookings(normalized);
    } catch (e) {
      console.error('Failed to filter uploaded bookings', e);
      setExistingBookings([]);
    }
  };

  const resetForm = () => {
    setFile(null);
    setParsedData([]);
    setValidationResults([]);
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = validationResults.filter(r => r.isValid).length;
  const invalidCount = validationResults.filter(r => !r.isValid).length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Multiple Booking Upload</h1>
        <p className="text-gray-600">Upload multiple bookings using an Excel file</p>
      </div>

      {/* Step 1: Select Customer */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Select Customer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CustomerAutocomplete
            label="Customer *"
            placeholder="Type customer ID"
            onChange={(c) => setSelectedCustomer(c)}
            value={selectedCustomer?.id}
            valueLabel={selectedCustomer ? `${selectedCustomer.customer_id} - ${selectedCustomer.customer_name}` : ''}
          />
          <div className="flex items-end">
            <button
              type="button"
              onClick={fetchCustomerBookings}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Fetch Customer Bookings
            </button>
          </div>
        </div>
      </div>

      {/* Sample File Download */}
      <div className="bg-primary-50 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-primary-900 mb-2">Download Sample Template</h3>
            <p className="text-primary-700 mb-4">
              Download the sample Excel template to see the required format for your data.
            </p>
          </div>
          <button
            onClick={downloadSampleExcel}
            className="flex items-center px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <Download className="h-5 w-5 mr-2" />
            Download Sample
          </button>
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Excel File</h3>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          {!file ? (
            <div>
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Click to select an Excel file or drag and drop</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
              >
                Select File
              </button>
            </div>
          ) : (
            <div>
              <FileSpreadsheet className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-900 font-medium mb-2">{file.name}</p>
              <p className="text-gray-600 mb-4">{parsedData.length} rows found</p>
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 mr-2"
              >
                Change File
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Validation Results */}
      {validationResults.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Validation Results</h3>
            <div className="flex space-x-4">
              <span className="flex items-center text-green-600">
                <CheckCircle className="h-5 w-5 mr-1" />
                {validCount} Valid
              </span>
              <span className="flex items-center text-red-600">
                <XCircle className="h-5 w-5 mr-1" />
                {invalidCount} Invalid
              </span>
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {validationResults.map((result, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${result.isValid
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
                  }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    Row {result.rowIndex}: {result.data['Customer Name']} - AWB {result.data['AWB Number']}
                  </span>
                  {result.isValid ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>

                {result.errors.length > 0 && (
                  <div className="text-red-700 text-sm">
                    <strong>Errors:</strong>
                    <ul className="list-disc list-inside ml-2">
                      {result.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.warnings.length > 0 && (
                  <div className="text-yellow-700 text-sm mt-2">
                    <strong>Warnings:</strong>
                    <ul className="list-disc list-inside ml-2">
                      {result.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {validCount > 0 && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleAddBookings}
                disabled={isUploading}
                className="px-6 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? 'Adding...' : `Add ${validCount} Valid Bookings`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bookings from uploaded file for selected customer */}
      {selectedCustomer && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">File Bookings for {selectedCustomer.customer_id}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AWB</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receiver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {existingBookings.map((b) => (
                  <tr key={b.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.awb_no}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.receiver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{parseFloat(b.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {existingBookings.length === 0 && (
                  <tr>
                    <td className="px-6 py-4 text-center text-gray-500" colSpan="4">No matching rows from file. Upload a file and click "Fetch Customer Bookings".</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Results */}
      {uploadResults && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Results</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{uploadResults.success}</div>
              <div className="text-green-700">Successfully Uploaded</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{uploadResults.failed}</div>
              <div className="text-red-700">Failed</div>
            </div>
            <div className="bg-primary-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">{uploadResults.total}</div>
              <div className="text-primary-700">Total Processed</div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700"
            >
              Upload Another File
            </button>
            <button
              onClick={() => navigate('/booking')}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
            >
              View Bookings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultipleBooking;
